import { isValid } from "date-fns";
import { DateRange, ErrorLogsFilters, Filters } from "./types";
import { PRODUCT_TYPE_MAP, SCRAPE_OPTIONS_MAP } from "./constants";

// Always send the precise ISO timestamp. The backend's formatDateForClickHouse
// preserves the time for ISO inputs (setUTCSeconds(59, 0) on end dates), so a
// local end-of-day stays at the correct UTC moment for the user's timezone.
// Bare yyyy-MM-dd dates would be pinned to UTC 23:59, which truncates today's
// data for users west of UTC.
export const formatDateForBackend = (date: Date | null): string => {
  if (!date || !isValid(date)) return "";
  return date.toISOString();
};

// Map UI filter options to backend format
export const mapFilterOptionsToBackend = (filters: Filters) => {
  const {
    productType,
    parameters = [],
    domains = [],
    countries = [],
  } = filters || {};
  const productMapping = productType ? PRODUCT_TYPE_MAP[productType] : {};

  const result: Record<string, unknown> = {};

  // Add array filters if non-empty
  if (domains.length > 0) result.domains = domains;
  if (countries.length > 0) result.countries = countries;

  // Add product type filters
  if (productMapping.sourceProduct) result.sourceProduct = productMapping.sourceProduct;
  if (productMapping.structuredDataEndpoint) result.structuredDataEndpoint = productMapping.structuredDataEndpoint;

  // Map scrape options (e.g., "Render" -> { render: true })
  for (const param of parameters) {
    const backendField = SCRAPE_OPTIONS_MAP[param];
    if (backendField) result[backendField] = true;
  }

  return result;
};

// Build API parameters with date range and filters
export const buildAnalyticsApiParams = (
  dateRange: DateRange,
  filters?: Filters,
  extraParams?: Record<string, any>,
) => {
  const params: Record<string, any> = {
    startDate: formatDateForBackend(dateRange[0]),
    endDate: formatDateForBackend(dateRange[1]),
    ...(filters ? mapFilterOptionsToBackend(filters) : {}),
    ...extraParams,
  };

  // Convert statusCodes from string[] to number[] if present
  if (params.statusCodes) params.statusCodes = params.statusCodes.map(Number);

  return params;
};

// Extra params for the error-logs page's CSV export. Rule: each download
// matches the table the user is looking at — the error-logs page ignores global
// filters, so its export sends date range + these error-log filters only (same
// as its table fetch); the overview's error download sends global filters +
// status:"failure", matching its error preview.
export const buildErrorLogsDownloadParams = (
  errorLogsFilters?: Partial<ErrorLogsFilters>,
): Record<string, unknown> => ({
  ...(errorLogsFilters?.domains?.length ? { domains: errorLogsFilters.domains } : {}),
  ...(errorLogsFilters?.statusCodes?.length ? { statusCodes: errorLogsFilters.statusCodes } : {}),
  ...(errorLogsFilters?.severity ? { severity: errorLogsFilters.severity } : {}),
  ...(errorLogsFilters?.searchTerm ? { searchTerm: errorLogsFilters.searchTerm } : {}),
});
